/**
 * Renders nested J-code auth details inside an expanded order-auth table row.
 *
 * Uses oasis-ui-kit {@link DataGrid} for a unified grid layout (header + body)
 * instead of nesting {@link CustomTable}, which splits thead/tbody and breaks
 * styling inside expandable panels.
 */
import { DataGrid } from "@risalabs_frontend_org/oasis-ui-kit";
import { JSX, useMemo } from "react";
import { useSelector } from "react-redux";
import { AuthStatusOptionModel } from "../../data-model/authStatusOptions";
import { RootState } from "../../redux/store/store";
import NoData from "../noData/noData";
import {
  buildExpandedAuthDataGridColumns,
  generateExpandedAuthDataGridRows,
  mapAuthStatusOptionsToTagOptions,
} from "./expandedAuthTableRow";
import {
  ExpandableRowContentForOrderAuthProps,
  ExpandedAuthDataGridRow,
} from "./types";

/**
 * Renders the nested J-code DataGrid inside an expanded order-auth row.
 *
 * @param props - Auth-on-file data for the expanded parent order row
 * @returns Nested J-code DataGrid with tag-styled auth status column
 */
const ExpandableRowContentForOrderAuth = ({
  authOnFileData,
}: ExpandableRowContentForOrderAuthProps): JSX.Element => {
  const authStatusOptions = useSelector(
    (state: RootState): AuthStatusOptionModel[] => state.authStatusOptions.data,
  );

  const tagOptions = useMemo(
    () => mapAuthStatusOptionsToTagOptions(authStatusOptions),
    [authStatusOptions],
  );

  const columns = useMemo(
    () => buildExpandedAuthDataGridColumns(tagOptions),
    [tagOptions],
  );

  const rows = useMemo(
    () => generateExpandedAuthDataGridRows(authOnFileData),
    [authOnFileData],
  );

  return (
    <div className="expandable-row-content--container flex gap-4 p-4">
      <div className="no-content ml-2 border-r-1 border-primaryGray-15">
        &nbsp;
      </div>
      <div className="drug-table--container w-full rounded-drugTable bg-drugTableBg p-3">
        <div className="drug-table--header mb-3 text-overline font-bold">
          Drug Table
        </div>
        <div className="drug-table--body">
          <DataGrid<ExpandedAuthDataGridRow>
            columns={columns}
            rows={rows}
            emptyState={<NoData text="No J-codes found" />}
          />
        </div>
      </div>
    </div>
  );
};

export default ExpandableRowContentForOrderAuth;
